/**
 * One project: its numbers, its milestones, and the work on it.
 *
 * The budget panel is the reason this page exists. Booked minutes against
 * budgeted minutes and invoiced money against budgeted money are the two
 * questions a delivery manager opens a project to answer, and both are computed
 * by the server — nothing here adds up a page of rows to produce a figure.
 *
 * Tasks are the shared `ServerTable` pointed at `/tasks` with the project
 * fixed. Not a second table implementation: the same register, filtered.
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Pencil, Plus, Target, Trash2 } from 'lucide-react';
import {
  Alert,
  ApiError,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  DetailField,
  DetailGrid,
  ErrorState,
  LoadingState,
  PageHeader,
  StatCard,
  StatRow,
  StatusChip,
  Tabs,
  api,
  formatDate,
  formatDuration,
  formatMoney,
  toast,
} from '@connect/web-shared';

import {
  BILLING_TYPE_LABELS,
  MILESTONE_STATUS_TONES,
  PROJECT_STATUS_TONES,
} from '../../lib/tones';
import { MilestoneFormDialog } from './milestone-form-dialog';
import { ProjectFormDialog } from './project-form-dialog';
import { TasksTable } from '../tasks/tasks-table';

interface ProjectDetail {
  public_id: string;
  code: string;
  name: string;
  status: string;
  billing_type: string;
  client_public_id: string;
  client_name: string;
  manager_name: string | null;
  department_name: string | null;
  starts_on: string | null;
  ends_on: string | null;
  closed_on: string | null;
  currency: string;
  budget_amount: string | null;
  budget_minutes: number | null;
  booked_minutes: number;
  open_task_count: number;
  hourly_rate: string | null;
  client_visible: boolean;
  description: string | null;
  created_at: string;
}

interface Milestone {
  public_id: string;
  name: string;
  status: string;
  due_on: string | null;
  reached_on: string | null;
  amount: string | null;
  position: number;
  notes: string | null;
}

export function ProjectDetailPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [milestoneOpen, setMilestoneOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(null);
  const [removing, setRemoving] = useState<Milestone | null>(null);
  const [tab, setTab] = useState('tasks');

  const project = useQuery({
    queryKey: ['projects', publicId],
    queryFn: () => api.get<ProjectDetail>(`/projects/${publicId}`),
    enabled: Boolean(publicId),
  });

  const milestones = useQuery({
    queryKey: ['projects', publicId, 'milestones'],
    queryFn: () => api.get<Milestone[]>(`/projects/${publicId}/milestones`),
    enabled: Boolean(publicId),
  });

  const removeMilestone = useMutation({
    mutationFn: (milestone: Milestone) =>
      api.delete(`/projects/milestones/${milestone.public_id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects', publicId, 'milestones'] });
      toast.success('Milestone removed');
      setRemoving(null);
    },
    onError: (error) => {
      toast.error(
        'Could not remove it',
        error instanceof ApiError ? error.message : undefined,
      );
    },
  });

  const row = project.data;

  const burn = useMemo(() => {
    if (!row?.budget_minutes) return null;
    return Math.round((row.booked_minutes / row.budget_minutes) * 100);
  }, [row]);

  if (project.isLoading) return <LoadingState label="Opening the project" />;
  if (project.isError) {
    const error = project.error;
    return (
      <ErrorState
        title={error instanceof ApiError && error.isNotFound ? 'Project not found' : undefined}
        message={error instanceof ApiError ? error.message : undefined}
        requestId={error instanceof ApiError ? error.requestId : undefined}
        onRetry={() => void project.refetch()}
      />
    );
  }
  if (!row) return null;

  return (
    <div className="space-y-4">
      <PageHeader
        title={row.name}
        description={`${row.code} · ${row.client_name}`}
        breadcrumbs={[{ label: 'Projects', to: '/projects' }, { label: row.code }]}
        actions={
          <>
            <StatusChip value={row.status} tones={PROJECT_STATUS_TONES} />
            <Can permission="projects.project.edit">
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                <Pencil className="size-3.5" />
                Edit
              </Button>
            </Can>
          </>
        }
        tabs={
          <Tabs
            value={tab}
            onChange={setTab}
            items={[
              { id: 'tasks', label: 'Tasks', count: row.open_task_count },
              { id: 'milestones', label: 'Milestones', count: milestones.data?.length },
              { id: 'details', label: 'Details' },
            ]}
          />
        }
      />

      {/* The project is closed, so time cannot be booked against it. Said here
          rather than left for somebody to discover when a booking is refused. */}
      {row.status === 'completed' || row.status === 'cancelled' ? (
        <Alert tone="info" title={`This project is ${row.status}`}>
          New time and new tasks are refused against it. Reopen it by setting the status back to
          active if there is more to do.
        </Alert>
      ) : null}

      <StatRow className="sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Booked"
          value={formatDuration(row.booked_minutes)}
          hint={row.budget_minutes ? `of ${formatDuration(row.budget_minutes)} budgeted` : 'No time budget'}
          tone={burn !== null && burn > 100 ? 'danger' : burn !== null && burn > 85 ? 'warning' : 'accent'}
        />
        <StatCard
          label="Budget"
          value={formatMoney(row.budget_amount, row.currency)}
          hint={row.hourly_rate ? `${formatMoney(row.hourly_rate, row.currency)} an hour` : 'Rate from the client'}
        />
        <StatCard
          label="Open tasks"
          value={String(row.open_task_count)}
          hint={row.open_task_count === 0 ? 'Nothing outstanding' : 'Not yet done or cancelled'}
          tone={row.open_task_count > 0 ? 'warning' : 'success'}
        />
        <StatCard
          label="Billing"
          value={BILLING_TYPE_LABELS[row.billing_type] ?? row.billing_type}
          hint={row.billing_type === 'non_billable' ? 'Time here never reaches an invoice' : undefined}
        />
      </StatRow>

      {tab === 'tasks' ? (
        <TasksTable
          projectPublicId={row.public_id}
          projectName={row.name}
          stateKey={`projects.${row.public_id}.tasks`}
        />
      ) : null}

      {tab === 'milestones' ? (
        <Card>
          <CardHeader
            title="Milestones"
            description="Points in the work that can be invoiced on their own."
            actions={
              <Can permission="projects.milestone.create">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setEditingMilestone(null);
                    setMilestoneOpen(true);
                  }}
                >
                  <Plus className="size-3.5" />
                  Add
                </Button>
              </Can>
            }
          />
          <CardBody className="p-0">
            {(milestones.data ?? []).length === 0 ? (
              <p className="p-4 text-sm text-muted">
                None yet. A fixed-price project usually has one per payment stage; a
                time-and-materials one may have none at all.
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {(milestones.data ?? []).map((milestone) => (
                  <li key={milestone.public_id} className="flex items-center gap-3 px-4 py-2.5">
                    <Target className="size-4 shrink-0 text-faint" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-body">
                        {milestone.name}
                      </span>
                      <span className="block text-2xs text-faint">
                        {milestone.due_on ? `Due ${formatDate(milestone.due_on)}` : 'No date'}
                        {milestone.reached_on
                          ? ` · reached ${formatDate(milestone.reached_on)}`
                          : ''}
                      </span>
                    </span>
                    <span className="shrink-0 text-sm text-body tabular">
                      {formatMoney(milestone.amount, row.currency)}
                    </span>
                    <StatusChip value={milestone.status} tones={MILESTONE_STATUS_TONES} />
                    <Can permission="projects.milestone.edit">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Edit ${milestone.name}`}
                        onClick={() => {
                          setEditingMilestone(milestone);
                          setMilestoneOpen(true);
                        }}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                    </Can>
                    <Can permission="projects.milestone.delete">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove ${milestone.name}`}
                        // An invoiced milestone is not removable — the API
                        // refuses it, so the control says so first.
                        disabled={milestone.status === 'invoiced'}
                        onClick={() => setRemoving(milestone)}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </Can>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      ) : null}

      {tab === 'details' ? (
        <Card>
          <CardBody>
            <DetailGrid>
              <DetailField label="Client">
                <button
                  type="button"
                  className="text-accent hover:underline"
                  onClick={() => navigate(`/clients/${row.client_public_id}`)}
                >
                  {row.client_name}
                </button>
              </DetailField>
              <DetailField label="Manager">{row.manager_name ?? '—'}</DetailField>
              <DetailField label="Department">{row.department_name ?? '—'}</DetailField>
              <DetailField label="Starts">{formatDate(row.starts_on)}</DetailField>
              <DetailField label="Ends">{formatDate(row.ends_on)}</DetailField>
              <DetailField label="Closed">{formatDate(row.closed_on)}</DetailField>
              <DetailField label="Currency">{row.currency}</DetailField>
              <DetailField label="Client sees it">
                {row.client_visible ? (
                  <span className="inline-flex items-center gap-1 text-success">
                    <CheckCircle2 className="size-3.5" />
                    Yes
                  </span>
                ) : (
                  'No'
                )}
              </DetailField>
            </DetailGrid>
            {row.description ? (
              <p className="mt-4 whitespace-pre-line border-t border-line pt-4 text-sm text-body">
                {row.description}
              </p>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      <ProjectFormDialog
        open={editing}
        publicId={row.public_id}
        onClose={() => setEditing(false)}
        onCreated={() => {
          void queryClient.invalidateQueries({ queryKey: ['projects', publicId] });
        }}
      />

      <MilestoneFormDialog
        open={milestoneOpen}
        projectPublicId={row.public_id}
        currency={row.currency}
        milestone={editingMilestone}
        onClose={() => setMilestoneOpen(false)}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ['projects', publicId, 'milestones'] });
          setMilestoneOpen(false);
        }}
      />

      <Confirm
        open={removing !== null}
        title="Remove this milestone?"
        message={
          removing
            ? `"${removing.name}" will be removed from ${row.name}. Any tasks pointing at it keep their work and lose the milestone.`
            : ''
        }
        confirmLabel="Remove"
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (removing) await removeMilestone.mutateAsync(removing);
        }}
      />
    </div>
  );
}
